import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { PiCaretLeft, PiCaretRight } from 'react-icons/pi';
import './styles.css';

const EDGE_TOLERANCE_PX = 2;

const HorizontalChipScroller = ({ children, className = '', ariaLabel }) => {
  const viewportRef = useRef(null);
  const [scrollState, setScrollState] = useState({
    hasOverflow: false,
    canScrollLeft: false,
    canScrollRight: false,
  });

  const updateScrollState = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    const maxScrollLeft = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    const nextState = {
      hasOverflow: maxScrollLeft > EDGE_TOLERANCE_PX,
      canScrollLeft: viewport.scrollLeft > EDGE_TOLERANCE_PX,
      canScrollRight: viewport.scrollLeft < maxScrollLeft - EDGE_TOLERANCE_PX,
    };

    setScrollState((current) => (
      current.hasOverflow === nextState.hasOverflow
      && current.canScrollLeft === nextState.canScrollLeft
      && current.canScrollRight === nextState.canScrollRight
        ? current
        : nextState
    ));
  }, []);

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return undefined;

    updateScrollState();
    const frame = window.requestAnimationFrame(updateScrollState);
    const resizeObserver = typeof ResizeObserver === 'function'
      ? new ResizeObserver(updateScrollState)
      : null;

    resizeObserver?.observe(viewport);
    window.addEventListener('resize', updateScrollState);

    return () => {
      window.cancelAnimationFrame(frame);
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateScrollState);
    };
  }, [children, updateScrollState]);

  const scrollByPage = (direction) => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    const distance = Math.min(280, Math.max(160, Math.round(viewport.clientWidth * 0.72)));
    viewport.scrollBy({ left: direction * distance, behavior: 'smooth' });
  };

  return (
    <div className={`nf-horizontal-chip-scroller ${scrollState.hasOverflow ? 'has-overflow' : ''}`}>
      <button
        type="button"
        className="nf-horizontal-chip-scroller__nav nf-horizontal-chip-scroller__nav--left"
        aria-label="Ver categorias anteriores"
        disabled={!scrollState.canScrollLeft}
        onClick={() => scrollByPage(-1)}
      >
        <PiCaretLeft aria-hidden="true" />
      </button>
      <div
        ref={viewportRef}
        className={className}
        role="group"
        aria-label={ariaLabel}
        tabIndex={scrollState.hasOverflow ? 0 : undefined}
        onScroll={updateScrollState}
      >
        {children}
      </div>
      <button
        type="button"
        className="nf-horizontal-chip-scroller__nav nf-horizontal-chip-scroller__nav--right"
        aria-label="Ver mais categorias"
        disabled={!scrollState.canScrollRight}
        onClick={() => scrollByPage(1)}
      >
        <PiCaretRight aria-hidden="true" />
      </button>
    </div>
  );
};

HorizontalChipScroller.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  ariaLabel: PropTypes.string.isRequired,
};

export default HorizontalChipScroller;